// SimProgressBar — determinate progress for long point-sweep simulations
// (JV, C-V, EQE). 2026-07 live-user feedback: these endpoints take ~0.5 s
// per solve point and the UI looked frozen.
//
// Two feeds (V1.0 job queue):
//   * `progress` prop ({current, total, stage} from polling the async job
//     endpoint) — REAL progress reported by the solver. Used when present.
//   * estimated fallback — the original elapsed/perPointSec estimate,
//     shown until the first poll returns (or for legacy sync flows).
//
// Fully self-contained: owns its timer (cleared on completion/unmount).
// The estimated bar advances against totalPoints × perPointSec, capped at
// ~90% until `active` flips false, then jumps to 100% briefly before
// hiding. The real bar caps at 99% until completion.

import React, { useEffect, useRef, useState } from 'react';

export default function SimProgressBar({
  active,
  totalPoints,
  perPointSec = 0.6,
  label = 'Simulating',
  c,               // themeColors object
  progress = null, // real job progress {current, total, stage} | null
}) {
  const [elapsed, setElapsed] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const wasActive = useRef(false);

  useEffect(() => {
    let interval = null;
    let timeout = null;
    if (active) {
      wasActive.current = true;
      setFinishing(false);
      setElapsed(0);
      const t0 = Date.now();
      interval = setInterval(() => setElapsed((Date.now() - t0) / 1000), 250);
    } else if (wasActive.current) {
      // Falling edge — response arrived (or errored): show 100% briefly.
      wasActive.current = false;
      setFinishing(true);
      timeout = setTimeout(() => setFinishing(false), 700);
    }
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [active]);

  if (!active && !finishing) return null;

  // Real (polled) progress wins over the local estimate.
  const real = !!(progress && Number(progress.total) > 0);
  let total, donePts, pct;
  if (real) {
    total = Math.max(1, Number(progress.total));
    donePts = Math.min(total, Math.max(0, Number(progress.current) || 0));
    pct = finishing ? 100 : Math.min(99, (donePts / total) * 100);
  } else {
    total = Math.max(1, Number(totalPoints) || 1);
    const estTotalSec = total * perPointSec;
    const rawPct = (elapsed / estTotalSec) * 100;
    pct = finishing ? 100 : Math.min(90, rawPct);
    donePts = finishing
      ? total
      : Math.min(total, Math.floor(elapsed / perPointSec));
  }

  const mono = 'IBM Plex Mono, monospace';
  const stageTxt = real && progress.stage ? ` · ${progress.stage}` : '';

  return (
    <div style={{ margin: '10px 0 14px', maxWidth: 560 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 12, color: c.textMuted, marginBottom: 4, fontFamily: mono,
      }}>
        <span>
          {finishing
            ? `${label}… done`
            : real
              ? `${label}… ${Math.round(pct)}%`
              : `${label}… ~${Math.round(pct)}% (estimated)`}
        </span>
        <span>
          {real
            ? `${donePts}/${total}${stageTxt} · ${Math.floor(elapsed)}s elapsed`
            : `~${donePts}/${total} points · ${Math.floor(elapsed)}s elapsed`}
        </span>
      </div>
      <div style={{
        height: 6, background: c.bgSecondary,
        border: `1px solid ${c.border}`,
        borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: finishing ? c.green : c.accent,
          borderRadius: 3,
          transition: 'width 0.3s ease',
        }} />
      </div>
      {!finishing && (
        <div style={{
          fontSize: 11, color: c.textMuted, marginTop: 4,
          textAlign: 'center', fontFamily: mono,
        }}>
          {real
            ? 'Live progress reported by the solver'
            : `Progress is estimated (~${perPointSec}s per point) — the UI is not frozen`}
        </div>
      )}
    </div>
  );
}
